(() => {
  'use strict';

  /*
   * Source-bound Flow for the non-Dodge accessory hubs.  This file deliberately
   * has a different root selector and diagnostics namespace from
   * lds-hub-banner-flow.js.  A hub wrapper is expected to look like:
   *
   *   <div data-lds-brand-flow="sill" data-lds-brand="BMW">
   *     <div data-brand-flow-stage><img ...><canvas></canvas></div>
   *     <button data-brand-flow-toggle>Preview Flow</button>
   *     <span data-brand-flow-status></span>
   *   </div>
   *
   * data-banner-flow-stage / data-banner-flow-toggle /
   * data-banner-flow-status are accepted as compatibility aliases because the
   * existing hub markup uses those names.  The wrapper itself remains distinct
   * from the Dodge controller's data-hub-banner-flow root.
   */

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const SOURCE_WIDTH = 1536;
  const SOURCE_HEIGHT = 1024;
  const PIXEL_COUNT = SOURCE_WIDTH * SOURCE_HEIGHT;
  const FRAME_INTERVAL = 1000 / 30;
  const FLOW_PERIOD = 6500;
  const FLOW_SELECTOR = '[data-lds-brand-flow]';

  const freezePoints = points => Object.freeze(points.map(point => Object.freeze(point.slice())));
  const polygon = (points, options = {}) => Object.freeze({
    shape: 'polygon',
    points: freezePoints(points),
    detect: options.detect || 'white',
    minLevel: options.minLevel,
    minChroma: options.minChroma,
    maxSpread: options.maxSpread,
    minDarkNeighbors: options.minDarkNeighbors,
    contrastRadius: options.contrastRadius,
    darkNeighborDelta: options.darkNeighborDelta,
    halo: options.halo || 0,
    haloMin: options.haloMin,
    haloOpacity: options.haloOpacity,
    redRatio: options.redRatio,
    redDelta: options.redDelta,
    haloRedRatio: options.haloRedRatio,
    haloRedDelta: options.haloRedDelta,
    haloNeutralize: options.haloNeutralize,
    softHalo: options.softHalo,
    haloAll: options.haloAll,
  });
  const path = (points, options = {}) => Object.freeze({
    shape: 'path',
    points: freezePoints(points),
    width: options.width || 10,
    coreWidth: options.coreWidth || Math.max(3, (options.width || 10) * 0.55),
    detect: options.detect || 'white',
    minLevel: options.minLevel,
    minChroma: options.minChroma,
    maxSpread: options.maxSpread,
    minDarkNeighbors: options.minDarkNeighbors,
    contrastRadius: options.contrastRadius,
    darkNeighborDelta: options.darkNeighborDelta,
    halo: options.halo || 0,
    haloMin: options.haloMin,
    haloOpacity: options.haloOpacity,
    redRatio: options.redRatio,
    redDelta: options.redDelta,
    haloRedRatio: options.haloRedRatio,
    haloRedDelta: options.haloRedDelta,
    haloNeutralize: options.haloNeutralize,
    softHalo: options.softHalo,
    haloAll: options.haloAll,
  });

  /*
   * Every polygon/path is intentionally narrow and source-aligned.  The
   * detector still has to find a bright/colorful source pixel inside it; the
   * geometry alone can never create an LED shape over the surrounding car.
   */
  const CITROEN_SOURCE_RED_CONFIG = Object.freeze({
    sill: Object.freeze({
      emissionModel: 'source-red',
      preserveNeutralRed: true,
      neutralWhiteCap: 70,
      names: Object.freeze([
        'lds-c-elysee-metal-seal-red-v4.png',
        'lds-c-elysee-metal-seal-red-v3.png',
      ]),
      zones: Object.freeze([
        path([[409, 294], [594, 398]], {
          detect: 'red', minLevel: 150, minChroma: 40,
          redRatio: 1.12, redDelta: 40,
          width: 18, coreWidth: 9, halo: 7, haloMin: 25,
          haloNeutralize: false, haloRedDelta: 18, haloRedRatio: 1.05,
          haloOpacity: 0.14, softHalo: false,
        }),
        polygon([[615, 385], [917, 545], [910, 605], [608, 455]], {
          detect: 'red', minLevel: 150, minChroma: 40,
          redRatio: 1.12, redDelta: 40,
          halo: 7, haloMin: 25,
          haloNeutralize: false, haloRedDelta: 18, haloRedRatio: 1.05,
          haloOpacity: 0.14, softHalo: false,
        }),
        path([[935, 593], [1157, 729]], {
          detect: 'red', minLevel: 150, minChroma: 40,
          redRatio: 1.12, redDelta: 40,
          width: 18, coreWidth: 9, halo: 7, haloMin: 25,
          haloNeutralize: false, haloRedDelta: 18, haloRedRatio: 1.05,
          haloOpacity: 0.14, softHalo: false,
        }),
        path([[333, 217], [313, 253], [311, 275], [315, 289], [1190, 810],
          [1215, 819], [1240, 815], [1296, 790], [1320, 772]], {
          detect: 'red', minLevel: 150, minChroma: 40,
          redRatio: 1.12, redDelta: 40,
          width: 7, coreWidth: 3, halo: 5, haloMin: 25,
          haloNeutralize: false, haloRedDelta: 18, haloRedRatio: 1.05,
          haloOpacity: 0.1, softHalo: false,
        }),
        // Existing face/edge spill plus the two narrow retained source-red
        // reflection strips. These detect red excess only; they never tint
        // blue or neutral source pixels.
        polygon([[340, 225], [1310, 775], [1235, 805], [325, 285]], {
          detect: 'red-reflection',
        }),
        polygon([[350, 294], [1205, 797], [1320, 788], [1325, 830],
          [1205, 848], [330, 324]], {detect: 'red-reflection'}),
        polygon([[330, 162], [1225, 651], [1225, 691], [330, 202]], {
          detect: 'red-reflection',
        }),
        polygon([[590, 497], [950, 712], [950, 752], [590, 537]], {
          detect: 'red-reflection',
        }),
      ]),
    }),
    badge: Object.freeze({
      emissionModel: 'source-red',
      preserveNeutralRed: true,
      neutralWhiteCap: 45,
      names: Object.freeze(['lds-c-elysee-badge-recess-v1.png']),
      zones: Object.freeze([
        polygon([[565, 665], [968, 665], [968, 835], [565, 835]], {
          detect: 'red', minLevel: 150, minChroma: 40,
          redRatio: 1.12, redDelta: 40,
          halo: 28, haloMin: 0, haloAll: true,
          haloNeutralize: false, haloRedDelta: 18, haloRedRatio: 1.05,
          haloOpacity: 0.12, softHalo: false,
        }),
        polygon([[570, 690], [963, 690], [963, 820], [570, 820]], {
          detect: 'red-reflection',
        }),
        polygon([[614, 792], [926, 792], [926, 817], [614, 817]], {
          detect: 'red-reflection',
        }),
      ]),
    }),
  });

  const CONFIG = Object.freeze({
    smart: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-fortwo-steel-red-v2.png'],
        zones: [
          path([[347,334],[540,419]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:10,haloMin:18}),
          polygon([[610,440],[812,516],[812,554],[606,481]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),
          path([[882,576],[1132,688]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:10,haloMin:18}),
          path([[280,261],[263,319],[264,331],[274,339]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:8,halo:10,haloMin:18}),
          path([[1224,773],[1240,771],[1305,718]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:8,halo:10,haloMin:18}),
          polygon([[258,331],[1231,778],[1303,719],[1310,770],[1230,839],[240,399]],{detect:'red-reflection'})
        ]
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-smart-badge-recess-v1.png'],
        zones: [
          polygon([[666,635],[873,635],[873,690],[666,690]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:18}),
          polygon([[628,696],[891,696],[891,739],[628,739]],{detect:'red-reflection'}),
          polygon([[435,845],[1080,845],[1080,883],[435,883]],{detect:'red-reflection'})
        ]
      }
    },
    skoda: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-octavia-matte-red-v2.png'],
        zones: [polygon([[615,454],[920,563],[900,635],[600,500]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18})]
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-octavia-badge-recess-v1.png'],
        zones: [
          polygon([[625,657],[905,657],[905,710],[625,710]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:18}),
          polygon([[605,717],[922,717],[922,755],[605,755]],{detect:'red-reflection'}),
          polygon([[603,794],[917,794],[917,845],[603,845]],{detect:'red-reflection'})
        ]
      }
    },
    tesla: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-model-s-matte-metal-seal-red-v1.png'],
        zones: [polygon([[569,458],[868,544],[867,594],[568,505]], {detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18})],
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-model-s-badge-recess-v1.png'],
        zones: [
          polygon([[578,593],[957,593],[957,636],[578,636]], {detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),
          polygon([[562,644],[973,644],[973,676],[562,676]], {detect:'red-reflection'}),
        ],
      },
    },
    zeekr: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-001-metal-seal-red-v2.png'],
        zones: [
          path([[348,367],[598,482]], {detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:10,haloMin:18}),
          polygon([[631,478],[780,527],[777,584],[624,537]], {detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),
          path([[811,583],[1099,724]], {detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:10,haloMin:18}),
          path([[311,312],[279,369],[280,384],[1112,787],[1132,793],[1153,787],[1188,757],[1198,723]], {detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:10,haloMin:18}),
        ],
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-001-badge-recess-v4.png'],
        zones: [polygon([[705,678],[832,678],[832,736],[705,736]], {detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:18})],
      },
    },
    volvo: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-s90-metal-seal-red-v3.png'],
        zones: [
          path([[444,293],[674,403]], {detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:10,haloMin:18}),
          polygon([[704,404],[886,460],[880,508],[704,451]], {detect:'red',minLevel:110,minChroma:40,halo:11,haloMin:18}),
          path([[902,520],[1166,653]], {detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:10,haloMin:18}),
          path([[384,224],[366,273],[369,289],[1210,716],[1229,717],[1250,705],[1302,663]], {detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:12,haloMin:18}),
          polygon([[354,282],[1200,727],[1232,739],[1315,679],[1319,711],[1240,768],[1195,754],[341,311]], {detect:'red-reflection'}),
        ],
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-s90-badge-recess-v1.png'],
        zones: [
          polygon([[741,638],[839,638],[839,670],[741,670]], {detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:18}),
          polygon([[693,669],[886,669],[893,702],[687,702]], {detect:'red-reflection'}),
        ],
      },
    },
    volkswagen: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-arteon-metal-seal-red-v2.png'],
        zones: [
          path([[440,324],[581,399]], {detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:9,haloMin:18}),
          polygon([[610,403],[917,555],[917,600],[610,446]], {detect:'red',minLevel:110,minChroma:40,halo:11,haloMin:18}),
          path([[952,603],[1130,700]], {detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:9,haloMin:18}),
          path([[375,245],[369,300],[374,319],[1163,763],[1182,770],[1203,766],[1264,727]], {detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:12,haloMin:18}),
          polygon([[353,302],[1170,780],[1206,791],[1282,741],[1285,774],[1208,820],[1160,808],[345,335]], {detect:'red-reflection'}),
        ],
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-arteon-badge-recess-v2.png'],
        zones: [
          polygon([[653,622],[887,622],[887,650],[653,650]], {detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),
          polygon([[645,649],[895,649],[895,671],[645,671]], {detect:'red-reflection'}),
        ],
      },
    },
    suzuki: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-vitara-metal-seal-red-v2.png'],
        zones: [
          path([[414,315],[589,395]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:10,halo:12,haloMin:18}),
          polygon([[642,400],[931,521],[923,580],[635,456]],{detect:'red',minLevel:110,minChroma:40,halo:15,haloMin:18}),
          path([[978,583],[1215,698]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:10,halo:12,haloMin:18}),
          path([[322,322],[1271,790],[1290,794],[1309,785],[1371,715]],{detect:'red',minLevel:70,minChroma:20,width:14,coreWidth:7,halo:8,haloMin:12}),
          path([[462,290],[1372,707]],{detect:'red',minLevel:70,minChroma:20,width:7,coreWidth:4,halo:4,haloMin:15}),
          polygon([[308,322],[1270,790],[1380,710],[1385,748],[1318,825],[1250,829],[299,355]],{detect:'red-reflection'})
        ]
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-vitara-badge-recess-v2.png'],
        zones: [
          polygon([[611,613],[925,613],[925,673],[611,673]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:18}),
          polygon([[575,677],[965,677],[965,724],[575,724]],{detect:'red-reflection'})
        ]
      }
    },
    subaru: {
      sill: {
        preserveNeutralRed: true,
        neutralWhiteCap: 140,
        names: ['lds-forester-metal-seal-red-v2.png'],
        zones: [
          path([[378,308],[597,401]],{detect:'red',minLevel:110,minChroma:40,width:17,coreWidth:9,halo:11,haloMin:18}),
          polygon([[643,406],[883,493],[880,538],[629,445]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:18}),
          path([[921,547],[1207,672]],{detect:'red',minLevel:110,minChroma:40,width:17,coreWidth:9,halo:11,haloMin:18}),
          path([[309,279],[303,296],[311,306],[1242,730],[1260,734],[1276,727],[1327,684]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:12,haloMin:18}),
          path([[306,288],[305,299],[310,303],[500,388],[900,568],[1200,705],[1260,731],[1274,728],[1300,712],[1328,685]],{detect:'white-led',minLevel:230,maxSpread:45,width:9,coreWidth:5,halo:0,minDarkNeighbors:2}),
          polygon([[288,300],[1245,730],[1336,680],[1340,716],[1278,766],[1234,770],[285,340]],{detect:'red-reflection'})
        ]
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-forester-badge-recess-v1.png'],
        zones: [
          polygon([[598,608],[933,608],[933,655],[598,655]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:18}),
          polygon([[580,653],[946,653],[946,701],[580,701]],{detect:'red-reflection'})
        ]
      }
    },
    seat: {
      sill: {
        preserveNeutralRed: true,
        names: ['lds-leon-metal-seal-red-v3.png'],
        zones: [
          path([[347,301],[591,431]],{detect:'red',minLevel:110,minChroma:40,width:17,coreWidth:9,halo:10,haloMin:18}),
          polygon([[631,440],[859,540],[859,587],[624,487]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),
          path([[889,595],[1183,760]],{detect:'red',minLevel:110,minChroma:40,width:20,coreWidth:10,halo:12,haloMin:18}),
          path([[292,231],[277,283],[281,302],[296,313],[1200,833],[1220,832],[1260,810],[1295,774],[1297,741]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:9,halo:13,haloMin:18})
        ]
      },
      badge: {
        preserveNeutralRed: true,
        names: ['lds-leon-badge-recess-v1.png'],
        zones: [
          polygon([[644,635],[946,635],[946,686],[644,686]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:18}),
          polygon([[632,728],[945,728],[945,760],[632,760]],{detect:'red-reflection'})
        ]
      }
    },
    rivian:{sill:{preserveNeutralRed:true,names:['lds-r1t-metal-seal-red-v2.png'],zones:[path([[369,387],[539,456]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:8,halo:9,haloMin:18}),polygon([[589,457],[873,560],[873,608],[589,501]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),path([[913,620],[1118,710]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:8,halo:9,haloMin:18}),path([[293,310],[275,354],[272,375],[286,393],[1150,770],[1173,777],[1197,772],[1262,717]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:13,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-rivian-badge-recess-v1.png'],zones:[polygon([[540,728],[998,728],[998,808],[540,808]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:15}),polygon([[520,848],[1013,848],[1013,878],[520,878]],{detect:'red-reflection'}),polygon([[530,953],[1001,953],[1001,984],[530,984]],{detect:'red-reflection'})]}},
    renault:{sill:{preserveNeutralRed:true,names:['lds-megane-metal-seal-red-v2.png'],zones:[path([[434,348],[621,445]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:10,haloMin:18}),polygon([[636,437],[842,530],[842,569],[636,477]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18}),path([[858,571],[1091,702]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:10,haloMin:18}),path([[335,306],[332,320],[338,331]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[1146,762],[1163,772],[1181,771],[1236,730]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-megane-badge-recess-v1.png'],zones:[polygon([[532,737],[998,737],[998,801],[532,801]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:15}),polygon([[525,820],[1007,820],[1007,872],[525,872]],{detect:'red-reflection'})]}},
    ram:{sill:{preserveNeutralRed:true,names:['lds-ram1500-metal-seal-red-v2.png'],zones:[path([[286,367],[477,437]],{detect:'red',minLevel:110,minChroma:40,width:17,coreWidth:9,halo:10,haloMin:18}),polygon([[522,439],[924,572],[924,615],[522,485]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:18}),path([[969,619],[1224,712]],{detect:'red',minLevel:110,minChroma:40,width:17,coreWidth:9,halo:10,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-ram-badge-recess-v1.png'],zones:[polygon([[591,721],[932,721],[932,785],[591,785]],{detect:'red',minLevel:110,minChroma:40,halo:15,haloMin:15}),polygon([[558,807],[940,807],[940,853],[558,853]],{detect:'red-reflection'})]}},
    rangerover:{sill:{preserveNeutralRed:true,names:['lds-vogue-metal-seal-red-v2.png'],zones:[path([[391,300],[624,414]],{detect:'red',minLevel:140,minChroma:50,width:14,coreWidth:7,halo:7,haloMin:25}),polygon([[665,411],[840,496],[840,538],[665,453]],{detect:'red',minLevel:140,minChroma:50,halo:10,haloMin:25}),path([[865,539],[1176,692]],{detect:'red',minLevel:140,minChroma:50,width:14,coreWidth:7,halo:7,haloMin:25}),path([[327,223],[319,278],[319,296],[326,307],[1228,774],[1247,780],[1265,776],[1287,758],[1326,705]],{detect:'red',minLevel:140,minChroma:50,width:16,coreWidth:8,halo:13,haloMin:25}),polygon([[713,570],[779,584],[782,616],[718,628]],{detect:'red-reflection'}),polygon([[1228,784],[1295,792],[1291,829],[1238,824]],{detect:'red-reflection'}),polygon([[329,370],[352,368],[373,378],[383,393],[378,411],[363,421],[342,414],[330,399],[326,382]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-vogue-badge-recess-v1.png'],zones:[polygon([[687,676],[856,676],[856,721],[687,721]],{detect:'red',minLevel:130,minChroma:50,halo:12,haloMin:20}),polygon([[688,783],[855,783],[855,808],[688,808]],{detect:'red-reflection'})]}},
    porsche:{sill:{preserveNeutralRed:true,names:['lds-cayenne-metal-seal-red-v2.png'],zones:[polygon([[635,454],[879,480],[879,535],[635,510]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]},badge:{preserveNeutralRed:true,names:['lds-cayenne-badge-recess-v1.png'],zones:[polygon([[550,676],[984,676],[984,743],[550,743]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15}),polygon([[538,755],[998,755],[998,800],[538,800]],{detect:'red-reflection'})]}},
    peugeot:{sill:{preserveNeutralRed:true,names:['lds-3008-metal-seal-red-v2.png'],zones:[path([[344,357],[587,445]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),polygon([[626,433],[831,495],[831,555],[626,493]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),path([[879,558],[1176,675]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),path([[273,289],[266,343],[269,361],[1237,743],[1257,740],[1274,728],[1312,677]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:7,halo:8,haloMin:18}),path([[276,282],[500,365],[800,474],[1050,569],[1311,667]],{detect:'red',minLevel:25,minChroma:14,width:9,coreWidth:4,halo:4,haloMin:12}),polygon([[251,334],[1326,665],[1282,751],[1230,769],[242,380]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-3008-badge-recess-v1.png'],zones:[polygon([[606,740],[922,740],[922,813],[606,813]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    opel:{sill:{preserveNeutralRed:true,names:['lds-crossland-metal-seal-red-v4.png'],zones:[path([[434,343],[560,408]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),polygon([[580,402],[862,527],[862,581],[580,458]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),path([[875,574],[1049,668]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),path([[409,269],[374,335],[375,352],[1044,722],[1065,727],[1080,720],[1146,659]],{detect:'red',minLevel:110,minChroma:40,width:15,coreWidth:7,halo:8,haloMin:18}),polygon([[361,321],[1162,645],[1090,750],[1034,752],[350,373]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-crossland-badge-recess-v1.png'],zones:[polygon([[526,753],[1003,753],[1003,820],[526,820]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    nissan:{sill:{preserveNeutralRed:true,names:['lds-murano-metal-seal-red-v2.png'],zones:[path([[402,342],[605,416]],{detect:'red',minLevel:140,minChroma:50,width:14,coreWidth:7,halo:6,haloMin:25}),polygon([[624,404],[862,486],[862,537],[624,455]],{detect:'red',minLevel:140,minChroma:50,halo:9,haloMin:25}),path([[885,528],[1162,640]],{detect:'red',minLevel:140,minChroma:50,width:14,coreWidth:7,halo:6,haloMin:25}),path([[279,327],[1280,741],[1297,740],[1327,691],[1329,680]],{detect:'red',minLevel:140,minChroma:50,width:13,coreWidth:6,halo:8,haloMin:25})]},badge:{preserveNeutralRed:true,names:['lds-murano-badge-recess-v1.png'],zones:[polygon([[547,764],[985,764],[985,839],[547,839]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    oldsmobile:{sill:{preserveNeutralRed:true,names:['lds-aurora-metal-seal-red-v4.png'],zones:[path([[377,330],[508,395]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),polygon([[546,403],[952,596],[952,658],[546,465]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),path([[1007,649],[1182,739]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:18}),path([[315,251],[269,306],[269,326],[1281,834],[1296,829],[1339,762],[1338,745],[328,252],[315,251]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:7,haloMin:18}),polygon([[258,296],[1350,732],[1325,826],[1288,861],[246,344]],{detect:'red-reflection'})]}},
    mitsubishi:{sill:{preserveNeutralRed:true,names:['lds-outlander-metal-seal-red-v2.png'],zones:[polygon([[585,407],[891,516],[891,579],[585,471]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-outlander-badge-recess-v1.png'],zones:[polygon([[490,755],[1045,755],[1045,820],[490,820]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    mazda:{sill:{preserveNeutralRed:true,names:['lds-mazda3-metal-seal-red-v2.png'],zones:[polygon([[631,407],[880,508],[880,574],[631,477]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-mazda3-badge-recess-v1.png'],zones:[polygon([[605,746],[925,746],[925,810],[605,810]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    maserati:{sill:{preserveNeutralRed:true,names:['lds-ghibli-metal-seal-red-v3.png'],zones:[path([[437,348],[543,397]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:7,haloMin:18}),polygon([[587,392],[906,531],[906,594],[587,457]],{detect:'red',minLevel:110,minChroma:40,halo:9,haloMin:18}),path([[949,602],[1085,668]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:7,haloMin:18}),path([[321,324],[1170,763],[1184,757],[1226,701],[1227,687],[370,271],[360,275],[322,314],[321,324]],{detect:'red',minLevel:110,minChroma:40,width:10,coreWidth:5,halo:7,haloMin:18}),polygon([[308,310],[1240,677],[1193,785],[1160,791],[300,348]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-ghibli-badge-recess-v1.png'],zones:[polygon([[633,716],[900,716],[900,775],[633,775]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15}),polygon([[616,785],[926,785],[926,829],[616,829]],{detect:'red-reflection'})]}},
    lincoln:{sill:{preserveNeutralRed:true,names:["lds-nautilus-metal-seal-red-v2.png"],zones:[path([[509,638],[615,609]],{detect:"red",minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),polygon([[644,565],[951,497],[951,548],[644,617]],{detect:"red",minLevel:110,minChroma:40,halo:9,haloMin:18}),path([[978,509],[1068,486]],{detect:"red",minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[323,646],[354,698],[375,716],[394,718],[1187,495],[1198,478],[1213,419]],{detect:"red",minLevel:110,minChroma:40,width:15,coreWidth:8,halo:8,haloMin:18}),polygon([[309,632],[1228,406],[1220,510],[404,754],[352,738],[300,677]],{detect:"red-reflection"}),polygon([[1320,265],[1415,250],[1410,300],[1390,340],[1355,365],[1315,355],[1290,330],[1300,295]],{detect:"red-reflection"})]},badge:{preserveNeutralRed:true,names:['lds-nautilus-badge-recess-v1.png'],zones:[polygon([[423,774],[1110,774],[1110,845],[423,845]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    lexus:{sill:{preserveNeutralRed:true,names:['lds-gx550-metal-seal-red-v2.png'],zones:[polygon([[580,401],[934,549],[934,620],[580,472]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-gx550-badge-recess-v1.png'],zones:[polygon([[559,714],[994,714],[994,776],[559,776]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15}),polygon([[546,804],[1005,804],[1005,834],[546,834]],{detect:'red-reflection'})]}},
    landrover:{sill:{preserveNeutralRed:true,names:['lds-discovery-metal-seal-red-v2.png'],zones:[path([[396,306],[578,394]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),polygon([[615,393],[909,521],[909,563],[615,433]],{detect:'red',minLevel:110,minChroma:40,halo:9,haloMin:18}),path([[934,571],[1152,683]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[320,242],[298,281],[299,298],[1248,780],[1270,781],[1309,755],[1328,725]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:7,haloMin:18}),polygon([[284,268],[1340,714],[1320,770],[1265,812],[280,322]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-discovery-badge-recess-v1.png'],zones:[polygon([[620,777],[918,777],[918,816],[620,816]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    lancia:{sill:{preserveNeutralRed:true,names:['lds-ypsilon-metal-seal-red-v2.png'],zones:[path([[331,308],[605,420]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),polygon([[659,413],[819,461],[819,520],[659,478]],{detect:'red',minLevel:110,minChroma:40,halo:11,haloMin:18}),path([[877,537],[1212,683]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[245,229],[225,277],[225,294],[238,311],[1260,769],[1283,769],[1302,758],[1354,699]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:8,haloMin:18}),polygon([[208,217],[1374,682],[1332,792],[1250,810],[196,331]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-ypsilon-badge-recess-v1.png'],zones:[polygon([[675,708],[854,708],[854,784],[675,784]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:15}),polygon([[635,785],[890,785],[890,864],[635,864]],{detect:'red-reflection'})]}},
    kia:{sill:{preserveNeutralRed:true,names:['lds-sorento-metal-seal-red-v2.png'],zones:[path([[405,423],[580,482]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),polygon([[604,478],[870,548],[870,591],[604,521]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),path([[900,592],[1106,667]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[342,359],[325,410],[326,431],[1153,739],[1177,737],[1195,723],[1248,657]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:8,haloMin:18}),polygon([[312,346],[1260,640],[1230,744],[1175,788],[300,465]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-sorento-badge-recess-v1.png'],zones:[polygon([[593,693],[945,693],[945,746],[593,746]],{detect:'red',minLevel:110,minChroma:40,halo:14,haloMin:15}),polygon([[568,650],[969,650],[969,793],[568,793]],{detect:'red-reflection'})]}},
    infiniti:{sill:{preserveNeutralRed:true,names:['lds-qx70-metal-seal-red-v2.png'],zones:[polygon([[700,478],[843,520],[843,575],[700,535]],{detect:'red',minLevel:100,minChroma:35,halo:14,haloMin:15})]},badge:{preserveNeutralRed:true,names:['lds-qx70-badge-recess-v2.png'],zones:[polygon([[670,690],[842,690],[842,755],[670,755]],{detect:'red',minLevel:100,minChroma:35,halo:14,haloMin:15}),polygon([[580,753],[885,749],[906,812],[580,815]],{detect:'red-reflection'}),polygon([[590,905],[914,900],[920,990],[580,994]],{detect:'red-reflection'})]}},
    hyundai:{sill:{preserveNeutralRed:true,names:['lds-palisade-metal-seal-red-v3.png'],zones:[path([[428,420],[518,456]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),polygon([[539,455],[938,596],[938,640],[539,494]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18}),path([[974,639],[1075,682]],{detect:'red',minLevel:110,minChroma:40,width:14,coreWidth:7,halo:8,haloMin:18}),path([[372,349],[354,417],[357,437],[1098,745],[1116,746],[1131,738],[1194,672]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:8,haloMin:18}),polygon([[345,338],[1210,665],[1140,780],[1080,775],[335,456]],{detect:'red-reflection'}),polygon([[363,511],[1133,836],[1127,849],[357,524]],{detect:'red',minLevel:120,minChroma:35,redRatio:1.2,redDelta:35,halo:0})]},badge:{preserveNeutralRed:true,names:['lds-palisade-badge-recess-v2.png'],zones:[polygon([[538,671],[1004,671],[1004,717],[538,717]],{detect:'red',minLevel:110,minChroma:40,halo:13,haloMin:15})]}},
    hummer:{sill:{preserveNeutralRed:true,names:['lds-h2-metal-seal-red-v3.png'],zones:[path([[347,366],[607,481]],{detect:'red',minLevel:100,minChroma:35,width:15,coreWidth:7,halo:9,haloMin:18}),polygon([[648,488],[814,536],[814,587],[648,539]],{detect:'red',minLevel:100,minChroma:35,halo:9,haloMin:18}),path([[862,601],[1182,762]],{detect:'red',minLevel:100,minChroma:35,width:15,coreWidth:7,halo:9,haloMin:18}),path([[267,299],[216,344],[219,356],[1271,872],[1293,871],[1330,825],[1332,787]],{detect:'red',minLevel:100,minChroma:35,width:12,coreWidth:6,halo:7,haloMin:18}),polygon([[205,335],[1345,780],[1340,840],[1300,908],[1230,900],[195,380]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-h2-badge-recess-v1.png'],zones:[polygon([[680,714],[856,714],[856,784],[680,784]],{detect:'red',minLevel:100,minChroma:35,halo:14,haloMin:15}),polygon([[645,784],[885,784],[885,842],[645,842]],{detect:'red-reflection'})]}},
    honda:{sill:{preserveNeutralRed:true,names:['lds-accord-metal-seal-red-v3.png'],zones:[path([[407,322],[546,394]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:22}),polygon([[575,401],[932,563],[932,621],[575,459]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:22}),path([[965,630],[1140,735]],{detect:'red',minLevel:110,minChroma:40,width:16,coreWidth:8,halo:8,haloMin:22}),path([[359,243],[346,311],[350,330],[1157,797],[1179,800],[1202,786],[1263,740]],{detect:'red',minLevel:110,minChroma:40,width:12,coreWidth:6,halo:6,haloMin:22}),polygon([[340,240],[1270,740],[1180,820],[335,335]],{detect:'red-reflection'}),polygon([[340,330],[1170,810],[1280,780],[1180,850],[320,360]],{detect:'red-reflection'})]},badge:{preserveNeutralRed:true,names:['lds-accord-badge-recess-v1.png'],zones:[polygon([[455,718],[1074,718],[1074,797],[455,797]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:15,haloOpacity:.15})]}},
    gmc:{sill:{preserveNeutralRed:true,names:['lds-yukon-metal-seal-red-v4.png'],zones:[path([[324,682],[542,617]],{detect:'red',minLevel:145,minChroma:65,width:16,coreWidth:8,halo:6,haloMin:25,haloOpacity:.13}),polygon([[598,581],[919,485],[922,528],[598,614]],{detect:'red',minLevel:145,minChroma:65,halo:6,haloMin:25,haloOpacity:.13}),path([[958,486],[1145,430]],{detect:'red',minLevel:145,minChroma:65,width:16,coreWidth:8,halo:6,haloMin:25,haloOpacity:.13}),path([[212,680],[220,714],[240,735],[261,739],[1204,451],[1212,442],[1208,428]],{detect:'red',minLevel:100,minChroma:55,width:6,coreWidth:3,halo:2,haloMin:80,haloOpacity:.08})]},badge:{preserveNeutralRed:true,names:['lds-yukon-badge-recess-v1.png'],zones:[polygon([[417,749],[1109,749],[1109,828],[417,828]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:15,haloOpacity:.15})]}},
    genesis:{sill:{preserveNeutralRed:true,names:['lds-g80-metal-seal-red-v3.png'],zones:[polygon([[606,421],[856,523],[856,579],[606,478]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:15,haloOpacity:.15})]},badge:{preserveNeutralRed:true,names:['lds-genesis-badge-recess-v1.png'],zones:[polygon([[622,677],[911,677],[911,746],[622,746]],{detect:'red',minLevel:110,minChroma:40,halo:12,haloMin:15,haloOpacity:.15})]}},
    geely:{sill:{emissionModel:"source-red",preserveNeutralRed:true,neutralWhiteCap:60,names:["lds-coolray-metal-seal-red-v2.png"],zones:[path([[386,389],[495,434]],{detect:"red",minLevel:130,minChroma:25,redRatio:1.1,redDelta:22,width:16,coreWidth:8,halo:7,haloMin:20,haloNeutralize:false,haloRedDelta:14,haloRedRatio:1.04,haloOpacity:0.12,softHalo:false}),polygon([[526,432],[610,435],[696,463],[790,500],[870,540],[902,575],[900,604],[870,615],[806,605],[742,590],[680,570],[615,548],[560,520],[530,490]],{detect:"red",minLevel:180,minChroma:25,redRatio:1.22,redDelta:90,halo:18,haloMin:30,haloNeutralize:false,haloRedDelta:18,haloRedRatio:1.03,haloOpacity:0.11,softHalo:false}),path([[938,634],[1061,686]],{detect:"red",minLevel:130,minChroma:25,redRatio:1.1,redDelta:22,width:16,coreWidth:8,halo:7,haloMin:20,haloNeutralize:false,haloRedDelta:14,haloRedRatio:1.04,haloOpacity:0.12,softHalo:false}),path([[269,298],[258,364],[262,381],[280,386],[330,410],[380,434],[430,458],[480,482],[530,506],[580,530],[630,554],[680,578],[730,602],[780,626],[830,650],[880,674],[930,698],[980,722],[1030,746],[1080,770],[1115,780],[1140,787],[1160,781],[1231,720]],{detect:"red",minLevel:100,minChroma:22,redRatio:1.08,redDelta:18,width:16,coreWidth:8,halo:20,haloMin:30,haloNeutralize:false,haloRedDelta:12,haloRedRatio:1.03,haloOpacity:0.12,softHalo:false})]},badge:{emissionModel:"source-red",preserveNeutralRed:true,neutralWhiteCap:18,names:["lds-coolray-badge-recess-v1.png"],zones:[polygon([[500,690],[1050,690],[1050,820],[500,820]],{detect:"red",minLevel:100,minChroma:22,redRatio:1.08,redDelta:18,halo:12,haloMin:12,haloNeutralize:false,haloRedDelta:12,haloRedRatio:1.03,haloOpacity:0.12,softHalo:false})]}},
    fiat:{sill:{emissionModel:"source-red",preserveNeutralRed:true,neutralWhiteCap:65,names:["lds-500x-metal-red-v2.png"],zones:[path([[373,352],[592,449]],{detect:"red",minLevel:130,minChroma:25,redRatio:1.1,redDelta:22,width:16,coreWidth:8,halo:7,haloMin:20,haloNeutralize:false,haloRedDelta:14,haloRedRatio:1.04,haloOpacity:0.12,softHalo:false}),polygon([[655,452],[728,461],[786,490],[846,520],[850,548],[827,560],[778,557],[735,545],[690,530],[660,512],[648,486]],{detect:"red",minLevel:180,minChroma:25,redRatio:1.22,redDelta:90,halo:18,haloMin:30,haloNeutralize:false,haloRedDelta:18,haloRedRatio:1.03,haloOpacity:0.11,softHalo:false}),path([[892,585],[1135,699]],{detect:"red",minLevel:130,minChroma:25,redRatio:1.1,redDelta:22,width:16,coreWidth:8,halo:7,haloMin:20,haloNeutralize:false,haloRedDelta:14,haloRedRatio:1.04,haloOpacity:0.12,softHalo:false}),path([[295,279],[285,330],[289,350],[300,360],[1170,768],[1200,775],[1224,763],[1274,707]],{detect:"red",minLevel:100,minChroma:22,redRatio:1.08,redDelta:18,width:16,coreWidth:8,halo:20,haloMin:30,haloNeutralize:false,haloRedDelta:12,haloRedRatio:1.03,haloOpacity:0.12,softHalo:false})]},badge:{emissionModel:"source-red",preserveNeutralRed:true,neutralWhiteCap:18,names:["lds-500x-badge-recess-v1.png"],zones:[polygon([[610,788],[930,788],[930,900],[610,900]],{detect:"red",minLevel:100,minChroma:22,redRatio:1.08,redDelta:18,halo:12,haloMin:12,haloNeutralize:false,haloRedDelta:12,haloRedRatio:1.03,haloOpacity:0.12,softHalo:false})]}},
    dacia:{sill:{preserveNeutralRed:true,names:['lds-duster-metal-seal-red-v2.png'],zones:[path([[338,412],[540,475]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:8,halo:9,haloMin:18}),polygon([[570,472],[855,563],[855,594],[570,504]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18,haloOpacity:.15}),path([[891,591],[1151,680]],{detect:'red',minLevel:110,minChroma:40,width:18,coreWidth:8,halo:9,haloMin:18})]},badge:{preserveNeutralRed:true,names:['lds-duster-badge-recess-v2.png'],zones:[polygon([[494,694],[1036,694],[1036,765],[494,765]],{detect:'red',minLevel:110,minChroma:40,halo:10,haloMin:18,haloOpacity:.15})]}},
    citroen: CITROEN_SOURCE_RED_CONFIG,
    chevrolet:{sill:{preserveNeutralRed:true,names:['lds-camaro-metal-seal-red-v2.png'],zones:[path([[357,285],[485,345]],{detect:'red',minLevel:120,minChroma:65,width:18,coreWidth:9,halo:8,haloMin:35}),polygon([[513,344],[980,554],[980,610],[513,400]],{detect:'red',minLevel:120,minChroma:65,halo:9,haloMin:35}),path([[1017,612],[1195,711]],{detect:'red',minLevel:120,minChroma:65,width:18,coreWidth:9,halo:8,haloMin:35}),path([[359,226],[304,298],[306,308],[1192,784],[1208,788],[1295,682],[1292,675],[1200,624],[1100,572],[1000,522],[900,473],[800,425],[700,379],[600,333],[500,287],[400,242],[359,226]],{detect:'red',minLevel:120,minChroma:100,width:10,coreWidth:4,halo:4,haloMin:70})]},badge:{names:['lds-camaro-badge-recess-v1.png'],zones:[polygon([[404,696],[1131,696],[1131,774],[404,774]],{detect:'red',minLevel:120,minChroma:40,halo:12,haloMin:18,haloOpacity:.16}),polygon([[404,782],[1131,782],[1131,820],[404,820]],{detect:'red-reflection',halo:5,haloMin:18,haloOpacity:.12})]}},
    chery:{sill:{preserveNeutralRed:true,names:['lds-tiggo-sill-metal-seal-red-v2.png'],zones:[path([[379,338],[574,427]],{"detect":"red","minLevel":120,"minChroma":40,"halo":10,"haloMin":18,"haloOpacity":0.16,"width":18,"coreWidth":9}),polygon([[615,415],[885,535],[885,585],[615,465]],{"detect":"red","minLevel":120,"minChroma":40,"halo":10,"haloMin":18,"haloOpacity":0.16}),path([[924,592],[1168,712]],{"detect":"red","minLevel":120,"minChroma":40,"halo":10,"haloMin":18,"haloOpacity":0.16,"width":18,"coreWidth":9}),path([[319,263],[285,320],[292,339],[1195,783],[1220,788],[1247,780],[1317,712]],{"detect":"red","minLevel":120,"minChroma":40,"halo":10,"haloMin":18,"haloOpacity":0.16,"width":18,"coreWidth":9})]},badge:{names:['lds-tiggo-badge-recess-v1.png'],zones:[polygon([[538,651],[1002,655],[1002,733],[538,726]],{"detect":"red","minLevel":120,"minChroma":40,"halo":14,"haloMin":18,"haloOpacity":0.16})]}},
    alfaromeo: {sill:{preserveNeutralRed:true,names:["lds-giulia-metal-seal-red-v3.png","giulia-metal-seal-red-v3.png"],zones:[path([[436,382],[506,412]],{"detect":"red","minLevel":120,"minChroma":40,"halo":9,"haloMin":35,"width":14,"coreWidth":7}),polygon([[545,398],[895,549],[895,605],[545,454]],{"detect":"red","minLevel":120,"minChroma":40,"halo":9,"haloMin":35}),path([[928,593],[1017,632]],{"detect":"red","minLevel":120,"minChroma":40,"halo":9,"haloMin":35,"width":14,"coreWidth":7}),path([[346,308],[334,348],[331,373],[340,389],[1088,721],[1100,717],[1120,707],[1140,692],[1160,676],[1182,658]],{"detect":"red","minLevel":120,"minChroma":40,"halo":8,"haloMin":35,"width":14,"coreWidth":7,"haloOpacity":0.16})]},badge:{names:["lds-giulia-badge-recess-v1.png","giulia-badge-recess-v1.png"],zones:[polygon([[566,664],[826,664],[826,714],[566,714]],{"detect":"red","minLevel":120,"minChroma":40,"halo":14,"haloMin":18,"haloOpacity":0.16})]}},
    audi: Object.freeze({
  sill: Object.freeze({
    names: Object.freeze([
      'lds-audi-a4-stainless-doorway-v1.png',
      'audi-doorway-v1.png',
    ]),
    zones: Object.freeze([
      // Left white bar, from the lower-left end toward the A4 mark.
      path([[744, 513], [471, 657]], {
        width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220,
        maxSpread: 80, minDarkNeighbors: 2, halo: 9, haloMin: 35,
      }),
      // The A4 artwork is kept inside its own source-aligned rectangle.
      polygon([[774, 414], [922, 414], [922, 505], [774, 505]], {
        detect: 'white-led', minLevel: 220, maxSpread: 80,
        minDarkNeighbors: 2, halo: 9, haloMin: 34,
      }),
      // Right white bar, from the upper-right end toward the A4 mark.
      path([[1115, 313], [900, 427]], {
        width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220,
        maxSpread: 80, minDarkNeighbors: 2, halo: 9, haloMin: 35,
      }),
      // Only the bright, visibly lit lower sill edge is included.  The path
      // ends where source contrast ends; it does not sweep across the car.
      path([[652, 595], [365, 741]], {
        width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220,
        maxSpread: 80, minDarkNeighbors: 2, halo: 8, haloMin: 35,
        haloOpacity: 0.16,
      }),
    ]),
  }),
  badge: Object.freeze({
    names: Object.freeze([
      'lds-audi-emblem-rear-v1.png',
      'audi-emblem-rear-v1.png',
    ]),
    zones: Object.freeze([
      // The red Four Rings are the only animated badge artwork.  The tight
      // ROI excludes the number plate, black frame, trunk and tail lamps.
      polygon([[735, 650], [870, 650], [870, 720], [735, 720]], {
        detect: 'red', minLevel: 120, minChroma: 40, halo: 14,
        haloMin: 18, haloOpacity: 0.16,
      }),
    ]),
  }),
}),
    bmw: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['bmw-x3-metal-seal-on-v2.png', 'lds-bmw-x3-matte-metal-seal-on-v2.png']),
        zones: Object.freeze([
          polygon([[630, 500], [862, 546], [852, 601], [630, 554]], {
            detect: 'white', minLevel: 135, maxSpread: 100, halo: 9, haloMin: 28,
          }),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['m3-badge-recess-v1.png', 'lds-m3-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[648, 700], [902, 699], [902, 774], [648, 774]], {
            detect: 'mixed', minLevel: 115, minChroma: 38, maxSpread: 112, halo: 8, haloMin: 24,
          }),
        ]),
      }),
    }),
    toyota: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['toyota-lc-metal-seal-v2.png', 'lds-toyota-lc-metal-seal-v2.png']),
        zones: Object.freeze([
          path([[406, 546], [489, 535]], {width: 18, coreWidth: 9, halo: 8, haloMin: 30}),
          polygon([[512, 500], [597, 499], [598, 557], [509, 559]], {
            detect: 'color', minLevel: 75, minChroma: 35, halo: 8, haloMin: 30,
          }),
          polygon([[612, 498], [1019, 451], [1026, 491], [619, 538]], {halo: 8, haloMin: 26}),
          polygon([[1043, 430], [1131, 432], [1130, 489], [1041, 489]], {
            detect: 'color', minLevel: 75, minChroma: 35, halo: 8, haloMin: 30,
          }),
          path([[1153, 452], [1232, 442]], {width: 18, coreWidth: 9, halo: 8, haloMin: 30}),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['toyota-wordmark-badge-recess-v1.png', 'lds-toyota-wordmark-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[705, 570], [891, 570], [891, 628], [705, 628]], {
            detect: 'mixed', minLevel: 115, minChroma: 34, maxSpread: 115, halo: 8, haloMin: 25,
          }),
        ]),
      }),
    }),
    ford: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['ford-metal-seal-v2.png', 'lds-ford-metal-seal-v2.png']),
        zones: Object.freeze([
          path([[339, 638], [625, 535]], {width: 18, coreWidth: 9, halo: 8, haloMin: 32}),
          polygon([[682, 438], [840, 438], [840, 526], [681, 526]], {halo: 8, haloMin: 30}),
          path([[899, 433], [1139, 345]], {width: 18, coreWidth: 9, halo: 8, haloMin: 32}),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['mustang-badge-recess-v1.png', 'lds-mustang-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[602, 686], [970, 686], [970, 738], [602, 738]], {
            detect: 'white', minLevel: 120, maxSpread: 112, halo: 9, haloMin: 27,
          }),
        ]),
      }),
    }),
    mercedes: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['brabus-w213-metal-seal-v2.png', 'lds-brabus-w213-stainless-metal-seal-v2.png']),
        zones: Object.freeze([
          path([[397, 351], [561, 413]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          polygon([[610, 422], [906, 517], [898, 562], [617, 460]], {
            detect: 'white-led', minLevel: 220, maxSpread: 80, minDarkNeighbors: 2,
            halo: 9, haloMin: 32,
          }),
          path([[967, 573], [1146, 646]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          path([[321, 281], [284, 342], [285, 357], [1214, 728], [1234, 730], [1248, 723], [1291, 656], [1292, 648]], {
            width: 24, coreWidth: 12, detect: 'white-led', minLevel: 190, maxSpread: 90,
            minDarkNeighbors: 2, halo: 14, haloMin: 38, haloOpacity: 0.16,
          }),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['mercedes-wordmark-badge-recess-v1.png', 'lds-mercedes-wordmark-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[640, 662], [944, 662], [944, 713], [640, 713]], {
            detect: 'white', minLevel: 120, maxSpread: 115, halo: 10, haloMin: 37, haloOpacity: 0.2,
          }),
          polygon([[642, 720], [954, 720], [954, 758], [642, 758]], {
            detect: 'white', minLevel: 100, maxSpread: 80, halo: 8, haloMin: 70, haloOpacity: 0.11,
          }),
        ]),
      }),
    }),
    acura: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['rdx-steel-doorway-v1.png', 'lds-rdx-iii-stainless-doorway-v1.png']),
        zones: Object.freeze([
          path([[399, 369], [583, 457]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          polygon([[622, 460], [842, 549], [833, 591], [622, 507]], {
            detect: 'white-led', minLevel: 220, maxSpread: 80, minDarkNeighbors: 2,
            halo: 9, haloMin: 34,
          }),
          path([[890, 613], [1095, 709]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          path([[334, 289], [311, 350], [315, 366], [1150, 794], [1173, 799], [1191, 788], [1271, 749]], {
            width: 24, coreWidth: 12, detect: 'white-led', minLevel: 190, maxSpread: 90,
            minDarkNeighbors: 2, halo: 14, haloMin: 38, haloOpacity: 0.16,
          }),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['badge-rear-recess-v1.png', 'lds-acura-badge-rear-context-visualization-v1.png']),
        zones: Object.freeze([
          polygon([[663, 584], [850, 584], [850, 630], [663, 630]], {
            // The photographed red halo contains low-level red pixels around
            // the bright glyph.  Keep them inside the same source-aligned
            // artwork ROI so their original red is neutralized as well.
            detect: 'red', minLevel: 120, minChroma: 40, halo: 14, haloMin: 18, haloOpacity: 0.16,
          }),
        ]),
      }),
    }),
    buick: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['envision-steel-metal-seal-v2.png', 'lds-envision-steel-metal-seal-v2.png']),
        zones: Object.freeze([
          path([[426, 432], [509, 469]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          polygon([[548, 474], [910, 631], [895, 668], [541, 510]], {
            detect: 'white-led', minLevel: 220, maxSpread: 80, minDarkNeighbors: 2,
            halo: 9, haloMin: 34,
          }),
          path([[943, 675], [1041, 718]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          path([[369, 361], [364, 428], [367, 442], [1068, 777], [1085, 776], [1101, 766], [1157, 725]], {
            width: 24, coreWidth: 12, detect: 'white-led', minLevel: 190, maxSpread: 90,
            minDarkNeighbors: 2, halo: 14, haloMin: 40, haloOpacity: 0.16,
          }),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['buick-badge-recess-v1.png', 'lds-buick-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[520, 700], [1115, 680], [1115, 752], [520, 766]], {
            detect: 'white', minLevel: 125, maxSpread: 115, halo: 10, haloMin: 43, haloOpacity: 0.18,
          }),
          polygon([[515, 828], [1075, 807], [1075, 840], [515, 860]], {
            detect: 'white', minLevel: 100, maxSpread: 80, halo: 8, haloMin: 70, haloOpacity: 0.1,
          }),
        ]),
      }),
    }),
    byd: Object.freeze({
      sill: Object.freeze({
        // Source-matched to the approved 1536 x 1024 BYD Atto 3 render.  The
        // image is an editorial visualization; these narrow ROIs preserve the
        // source plate outline and only animate its documented light zones.
        names: Object.freeze(['lds-byd-atto3-stainless-doorway-v1.png', 'byd-atto3-steel-doorway-v1.png']),
        zones: Object.freeze([
          path([[374, 401], [555, 485]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          polygon([[588, 486], [875, 620], [868, 646], [581, 512]], {
            detect: 'white-led', minLevel: 220, maxSpread: 80, minDarkNeighbors: 2,
            halo: 9, haloMin: 34,
          }),
          path([[906, 646], [1107, 744]], {
            width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
            minDarkNeighbors: 2, halo: 9, haloMin: 35,
          }),
          path([[286, 374], [278, 409], [287, 429], [1215, 813], [1238, 821], [1255, 811], [1268, 789]], {
            width: 22, coreWidth: 11, detect: 'white-led', minLevel: 190, maxSpread: 90,
            minDarkNeighbors: 2, halo: 12, haloMin: 38, haloOpacity: 0.16,
          }),
        ]),
      }),
    }),
    chrysler: Object.freeze({
      sill: Object.freeze({
        names: Object.freeze(['lds-chrysler-300c-matte-metal-seal-v2.png']),
        zones: Object.freeze([
          polygon([[620,410],[886,514],[875,552],[604,449]], {detect:'white',minLevel:160,maxSpread:65,halo:9,haloMin:45,haloOpacity:0.16}),
        ]),
      }),
      badge: Object.freeze({
        names: Object.freeze(['lds-300c-badge-recess-v1.png']),
        zones: Object.freeze([
          polygon([[495,721],[1120,725],[1120,813],[495,810]], {detect:'red',minLevel:65,minChroma:25,halo:16,haloMin:12,haloOpacity:0.18}),
          polygon([[455,807],[1150,809],[1150,881],[455,879]], {detect:'red-reflection',minLevel:45,minChroma:15,halo:10,haloMin:10,haloOpacity:0.12}),
        ]),
      }),
    }),
    jeep: Object.freeze({
  sill: Object.freeze({
    // Approved 1536 x 1024 Grand Cherokee WL Stainless visualization.
    names: Object.freeze([
      'lds-grand-cherokee-wl-jeep-stainless-doorway-v1.png',
      'grand-cherokee-doorway-v1.png',
    ]),
    zones: Object.freeze([
      path([[456, 424], [624, 500]], {
        width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
        minDarkNeighbors: 2, halo: 9, haloMin: 35,
      }),
      polygon([[643, 490], [770, 548], [760, 578], [635, 526]], {
        detect: 'white-led', minLevel: 220, maxSpread: 80, minDarkNeighbors: 2,
        halo: 9, haloMin: 34,
      }),
      path([[794, 576], [976, 661]], {
        width: 18, coreWidth: 9, detect: 'white-led', minLevel: 220, maxSpread: 80,
        minDarkNeighbors: 2, halo: 9, haloMin: 35,
      }),
      path([[412,355],[402,410],[400,423],[401,433],[406,440],[413,445],[424,450],[997,715],[1036,690],[1082,650]], {
        width: 22, coreWidth: 11, detect: 'white-led', minLevel: 190, maxSpread: 90,
        minDarkNeighbors: 2, halo: 12, haloMin: 38, haloOpacity: 0.16,
      }),
    ]),
  }),
  badge: Object.freeze({
    // Generated automotive badge recess uploaded at the URL in the template.
    // The retained 1254 x 1254 white reference verifies the Grand Cherokee
    // artwork; this ROI follows the red strip in the 1536 x 1024 hero.
    names: Object.freeze([
      'lds-grand-cherokee-badge-recess-v1.png',
      'grand-cherokee-badge-recess-v1.png',
    ]),
    zones: Object.freeze([
      polygon([[430, 680], [1110, 700], [1110, 810], [430, 790]], {
        detect: 'red', minLevel: 120, minChroma: 40, halo: 14, haloMin: 18, haloOpacity: 0.16,
      }),
      polygon([[390,776],[1150,805],[1150,863],[390,833]],{detect:'red-reflection',minLevel:18,minChroma:12,halo:8,haloMin:18,haloOpacity:0.12}),
    ]),
  }),
}),
    cadillac: Object.freeze({sill:Object.freeze({names:Object.freeze(['lds-escalade-steel-metal-seal-v2.png']),zones:Object.freeze([
path([[455,344],[576,400]],{width:18,coreWidth:9,detect:'white-led',minLevel:220,maxSpread:80,minDarkNeighbors:2,halo:9,haloMin:35}),
polygon([[620,410],[928,546],[910,586],[609,451]],{detect:'white-led',minLevel:220,maxSpread:80,minDarkNeighbors:2,halo:9,haloMin:34}),
path([[963,594],[1115,667]],{width:18,coreWidth:9,detect:'white-led',minLevel:220,maxSpread:80,minDarkNeighbors:2,halo:9,haloMin:35}),
path([[351,263],[347,307],[354,327],[373,344],[1175,749],[1197,752],[1220,745],[1277,700]],{width:22,coreWidth:11,detect:'white-led',minLevel:190,maxSpread:90,minDarkNeighbors:2,halo:12,haloMin:38,haloOpacity:0.16})
])}),badge:Object.freeze({names:Object.freeze(['lds-escalade-badge-recess-v1.png']),zones:Object.freeze([
polygon([[482,674],[1094,680],[1094,755],[482,750]],{detect:'red',minLevel:120,minChroma:40,halo:14,haloMin:18,haloOpacity:0.16}),
polygon([[450,768],[1120,780],[1120,842],[450,828]],{detect:'red-reflection',minLevel:18,minChroma:12,halo:8,haloMin:18,haloOpacity:0.12})
])})}),
/*
 * Candidate properties for the existing brand Flow CONFIG/BRAND_CONFIGS
 * object. Insert these properties into that object; do not replace the
 * existing runtime or create a second renderer. Every name is the exact
 * uploaded Shopify Files basename with the required `lds-` prefix.
 */
  // Exact-source emission masks: preserve neutral RGB channels and replace only red excess.
  // ROI boundaries lie outside retained emission; the runtime feathers their support.
  haval: {
    sill: {names: ['lds-h6-metal-seal-red-v3.png'], zones: [polygon([[285, 175], [390, 175], [1400, 690], [1415, 920], [1240, 850], [265, 360]], {detect: 'red-reflection'})]},
    badge: {names: ['lds-haval-badge-big-dog-recess-v1.png'], zones: [polygon([[550, 650], [980, 650], [985, 805], [545, 805]], {detect: 'red-reflection'})]},
  },
  jetour: {
    sill: {names: ['lds-x70-metal-seal-red-v3.png'], zones: [polygon([[325, 150], [425, 150], [1400, 660], [1410, 810], [1240, 820], [300, 335]], {detect: 'red-reflection'})]},
  },
  ssangyong: {
    sill: {names: ['lds-rexton-metal-seal-red-v1.png'], zones: [polygon([[220, 210], [320, 210], [1410, 680], [1410, 845], [1260, 880], [210, 415]], {detect: 'red-reflection'})]},
    badge: {names: ['lds-rexton-badge-recess-v1.png'], zones: [polygon([[450, 652], [1095, 652], [1100, 770], [445, 770]], {detect: 'red-reflection'})]},
  },
  vauxhall: {
    sill: {names: ['lds-crossland-x-metal-seal-red-v2.png'], zones: [polygon([[320, 165], [435, 165], [1415, 650], [1410, 840], [1240, 840], [300, 355]], {detect: 'red-reflection'})]},
    badge: {names: ['lds-insignia-badge-recess-v1.png'], zones: [polygon([[590, 675], [965, 675], [970, 800], [585, 800]], {detect: 'red-reflection'})]},
  },

  });

  const states = new Set();
  const statesByRoot = new WeakMap();
  const activeStates = new Set();
  let schedulerFrame = null;
  let flowTime = -1;
  let flowPalette = null;

  const clamp = value => Math.max(0, Math.min(255, Math.round(value)));
  const smoothstep = (edge0, edge1, value) => {
    if (edge0 === edge1) return value < edge0 ? 0 : 1;
    const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  };
  const hueRgb = hue => {
    const h = ((hue % 1) + 1) % 1 * 6;
    const sector = Math.floor(h);
    const f = h - sector;
    const q = 1 - f;
    switch (sector) {
      case 0: return [255, Math.round(f * 255), 0];
      case 1: return [Math.round(q * 255), 255, 0];
      case 2: return [0, 255, Math.round(f * 255)];
      case 3: return [0, Math.round(q * 255), 255];
      case 4: return [Math.round(f * 255), 0, 255];
      default: return [255, 0, Math.round(q * 255)];
    }
  };
  const isReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const hoverFine = () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true;
  const hidden = () => document.hidden === true;

  const basename = source => {
    if (!source) return '';
    try {
      const url = new URL(source, document.baseURI);
      const value = decodeURIComponent(url.pathname || '');
      return value.slice(value.lastIndexOf('/') + 1).toLowerCase();
    } catch (_error) {
      const value = String(source).split(/[?#]/, 1)[0];
      return value.slice(value.lastIndexOf('/') + 1).toLowerCase();
    }
  };

  const normaliseBrand = value => String(value || '')
    .trim().toLowerCase().replace(/[^a-z0-9]/g, '');

  const resolveKindBrand = root => {
    const value = String(root.getAttribute('data-lds-brand-flow') || '').trim().toLowerCase();
    const kindAttr = root.getAttribute('data-lds-brand-flow-kind') || root.getAttribute('data-flow-kind') || '';
    let kind = String(kindAttr).trim().toLowerCase();
    let brand = root.getAttribute('data-lds-brand') || root.getAttribute('data-hub-brand') || '';
    if (value === 'sill' || value === 'badge') kind = value;
    if (!kind && /(?:sill|badge)$/.test(value)) kind = value.match(/(?:sill|badge)$/)[0];
    if (!brand && kind && value.endsWith(kind)) brand = value.slice(0, -kind.length).replace(/[-_]+$/, '');
    if (!brand) {
      const hub = root.closest('[data-lds-accessories-hub]');
      brand = hub?.getAttribute('data-lds-hub-brand') || '';
    }
    const normalised = normaliseBrand(brand);
    // Shopify vehicle data uses all three spellings in existing hubs.
    const canonical = normalised === 'mercedesbenz' || normalised === 'mercedesben' ? 'mercedes' : normalised;
    return {brand: canonical, kind: kind === 'badge' ? 'badge' : kind === 'sill' ? 'sill' : ''};
  };

  const pointInPolygon = (x, y, points) => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
      const [xi, yi] = points[i];
      const [xj, yj] = points[j];
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  const distanceToSegment = (x, y, a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (dx === 0 && dy === 0) return Math.hypot(x - a[0], y - a[1]);
    const length = dx * dx + dy * dy;
    const t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / length));
    return Math.hypot(x - a[0] - t * dx, y - a[1] - t * dy);
  };
  const distanceToPath = (x, y, points) => {
    let distance = Infinity;
    for (let i = 1; i < points.length; i += 1) {
      distance = Math.min(distance, distanceToSegment(x, y, points[i - 1], points[i]));
    }
    return distance;
  };
  const distanceToPolygon = (x, y, points) => {
    let distance = Infinity;
    for (let i = 0; i < points.length; i += 1) {
      distance = Math.min(distance, distanceToSegment(x, y, points[i], points[(i + 1) % points.length]));
    }
    return distance;
  };
  const boundsForZone = zone => {
    const xs = zone.points.map(point => point[0]);
    const ys = zone.points.map(point => point[1]);
    const padding = zone.shape === 'path' ? zone.width + zone.halo : zone.halo;
    return [
      Math.max(0, Math.floor(Math.min(...xs) - padding)),
      Math.max(0, Math.floor(Math.min(...ys) - padding)),
      Math.min(SOURCE_WIDTH - 1, Math.ceil(Math.max(...xs) + padding)),
      Math.min(SOURCE_HEIGHT - 1, Math.ceil(Math.max(...ys) + padding)),
    ];
  };
  const geometry = (zone, x, y) => {
    if (zone.shape === 'path') {
      const distance = distanceToPath(x, y, zone.points);
      if (distance > zone.coreWidth) return {factor: 0, inside: false, distance};
      return {factor: smoothstep(zone.coreWidth, 0, distance), inside: distance <= zone.width, distance};
    }
    const inside = pointInPolygon(x, y, zone.points);
    const distance = inside ? 0 : distanceToPolygon(x, y, zone.points);
    return {factor: inside ? 1 : 0, inside, distance};
  };

  const signalFor = (data, offset, zone) => {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const spread = max - min;
    const level = min;
    const minLevel = zone.minLevel ?? (zone.detect === 'color' ? 75 : 120);
    const minChroma = zone.minChroma ?? 28;
    const maxSpread = zone.maxSpread ?? 105;

    if (zone.detect === 'red') {
      const redDelta = zone.redDelta ?? minChroma;
      const redRatio = zone.redRatio ?? 1.2;
      if (r < minLevel || r - Math.max(g, b) < redDelta || r < g * redRatio || r < b * redRatio) return 0;
      return smoothstep(minLevel, Math.min(255, minLevel + 110), r);
    }
    if (zone.detect === 'color') {
      if (max < minLevel || spread < minChroma) return 0;
      return smoothstep(minLevel, Math.min(255, minLevel + 120), max);
    }
    if (zone.detect === 'mixed' && spread >= minChroma && max >= minLevel) {
      return smoothstep(minLevel, Math.min(255, minLevel + 120), max);
    }
    if (level < minLevel || spread > maxSpread) return 0;
    if (zone.detect === 'white-led') {
      const index = offset / 4, x = index % SOURCE_WIDTH, y = Math.floor(index / SOURCE_WIDTH);
      const radius = zone.contrastRadius || 12;
      let dark = 0;
      for (const [dx, dy] of [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[1,-1],[-1,1],[1,1]]) {
        const nx = x + dx * radius, ny = y + dy * radius;
        if (nx < 0 || nx >= SOURCE_WIDTH || ny < 0 || ny >= SOURCE_HEIGHT) continue;
        const n = (ny * SOURCE_WIDTH + nx) * 4;
        if (Math.min(data[n], data[n+1], data[n+2]) < level - (zone.darkNeighborDelta || 42)) dark++;
      }
      if (dark < (zone.minDarkNeighbors || 2)) return 0;
      return smoothstep(minLevel, 250, level);
    }
    return smoothstep(minLevel, Math.min(255, minLevel + 120), level);
  };

  const sourceLuma = (data, offset) => Math.max(data[offset], data[offset + 1], data[offset + 2]);
  const sourceChroma = (data, offset) => {
    const max = Math.max(data[offset], data[offset + 1], data[offset + 2]);
    const min = Math.min(data[offset], data[offset + 1], data[offset + 2]);
    return max - min;
  };
  const sourceIsRed = (data, offset, zone, halo = false) => {
    const r = data[offset];
    const g = data[offset + 1];
    const b = data[offset + 2];
    const redDelta = halo ? (zone?.haloRedDelta ?? zone?.redDelta ?? 3) : (zone?.redDelta ?? 3);
    const redRatio = halo ? (zone?.haloRedRatio ?? zone?.redRatio ?? 1.18) : (zone?.redRatio ?? 1.18);
    return r >= 5 && r - Math.max(g, b) >= redDelta && r >= g * redRatio && r >= b * redRatio;
  };

  const addMask = (state, index, alpha, oldAlpha) => {
    if (alpha > state.maskAlpha[index]) state.maskAlpha[index] = alpha;
    if (oldAlpha > state.maskOldAlpha[index]) state.maskOldAlpha[index] = oldAlpha;
  };

  // A halo is allowed only where the source itself contains a plausible
  // reflected/emitting pixel.  The core pixels below are the authority for
  // where a halo may begin; this support check prevents a geometry boundary
  // from turning into a rectangular coloured frame.
  const sourceGlowSupport = (data, offset, zone) => {
    const luma = sourceLuma(data, offset);
    const redSupport = zone.detect === 'red' && sourceIsRed(data, offset, zone, true);
    const customRedHalo = zone.redDelta != null || zone.haloRedDelta != null || zone.haloRedRatio != null || zone.haloNeutralize;
    const minimum = customRedHalo ? (zone.haloMin ?? 28) : (redSupport ? Math.min(zone.haloMin ?? 28, 6) : (zone.haloMin ?? 28));
    if (zone.haloAll) return 1;
    if (luma < minimum) return 0;
    const brightness = smoothstep(minimum, Math.min(255, minimum + 105), luma);
    const chroma = sourceChroma(data, offset);
    if (redSupport) return Math.max(0.08, brightness);
    if (chroma >= 42) return brightness;
    // A neutral reflection must be visibly brighter than a dark plate or
    // trim.  Its strength still fades continuously from the source value.
    if (luma < minimum + 18) return 0;
    return brightness * 0.72;
  };

  const buildMask = (state, sourceData) => {
    state.maskAlpha = new Float32Array(PIXEL_COUNT);
    state.maskOldAlpha = new Float32Array(PIXEL_COUNT);
    state.maskReflection = new Float32Array(PIXEL_COUNT);
    const core = new Uint8Array(PIXEL_COUNT);
    const zones = state.config.zones;

    zones.forEach(zone => {
      const [left, top, right, bottom] = boundsForZone(zone);
      const corePixels = [];
      for (let y = top; y <= bottom; y += 1) {
        for (let x = left; x <= right; x += 1) {
          const index = y * SOURCE_WIDTH + x;
          const offset = index * 4;
          const area = geometry(zone, x, y);
          if (!area.factor) continue;
          if (zone.detect === 'red-reflection') {
            const data = sourceData.data;
            const excess = Math.max(0, data[offset] - Math.max(data[offset + 1], data[offset + 2]));
            const feather = smoothstep(0, 14, distanceToPolygon(x, y, zone.points));
            const strength = excess * feather;
            if (strength > 0.5) {
              state.maskReflection[index] = Math.max(state.maskReflection[index], strength);
              addMask(state, index, strength / 255, 0);
            }
            continue;
          }
          const signal = signalFor(sourceData.data, offset, zone);
          if (!signal) continue;
          const alpha = Math.max(0.08, Math.min(0.98, signal * area.factor));
          // A verified LED pixel has its original emission fully neutralized
          // before the RGB colour is applied.  This is essential for red
          // source artwork such as the Acura badge.
          addMask(state, index, alpha, ['red', 'color', 'mixed'].includes(zone.detect) ? 1 : alpha);
          core[index] = 1;
          corePixels.push([x, y]);
        }
      }

      if (!zone.halo || !corePixels.length) return;
      const haloOpacity = zone.haloOpacity ?? 0.2;
      const radius = Math.max(1, Math.ceil(zone.halo));
      for (const [coreX, coreY] of corePixels) {
        for (let dy = -radius; dy <= radius; dy += 1) {
          const y = coreY + dy;
          if (y < 0 || y >= SOURCE_HEIGHT) continue;
          for (let dx = -radius; dx <= radius; dx += 1) {
            const x = coreX + dx;
            if (x < 0 || x >= SOURCE_WIDTH) continue;
            const distance = Math.hypot(dx, dy);
            if (distance === 0 || distance > radius) continue;
            const index = y * SOURCE_WIDTH + x;
            if (core[index]) continue;
            const offset = index * 4;
            let support = sourceGlowSupport(sourceData.data, offset, zone);
            // Keep polygon halos inside the verified artwork/reflection support;
            // soften at its boundary rather than tinting adjacent number plates.
            if (zone.shape === 'polygon') {
              if (!pointInPolygon(x, y, zone.points)) continue;
              support *= smoothstep(0, 3, distanceToPolygon(x, y, zone.points));
            }
            if (!support) continue;
            const distanceFade = smoothstep(radius, 0, distance);
            const alpha = Math.min(0.22, distanceFade * support * haloOpacity);
            if (alpha <= 0.005) continue;
            const chroma = sourceChroma(sourceData.data, offset);
            // Coloured source reflection is emission too: remove it fully so
            // the RGB wave cannot sit on top of a residual red layer.
            const sourceRed = (zone.detect === 'red' || (state.config.preserveNeutralRed && zone.detect === 'mixed')) && sourceIsRed(sourceData.data, offset, zone, true);
            const oldAlpha = zone.haloNeutralize ? alpha : (sourceRed ? (zone.softHalo ? alpha : 1) : 0);
            addMask(state, index, alpha, oldAlpha);
          }
        }
      }
    });

    const indices = [];
    for (let index = 0; index < PIXEL_COUNT; index += 1) {
      if (state.maskAlpha[index] > 0 || state.maskOldAlpha[index] > 0) indices.push(index);
    }
    state.maskIndices = Uint32Array.from(indices);
    state.maskXs = Uint16Array.from(indices, index => index % SOURCE_WIDTH);
    state.maskCount = indices.length;
  };

  const copyImageData = (ctx, imageData) => {
    const copy = ctx.createImageData(SOURCE_WIDTH, SOURCE_HEIGHT);
    copy.data.set(imageData.data);
    return copy;
  };

  const getPalette = time => {
    if (flowTime === time && flowPalette) return flowPalette;
    flowTime = time;
    const phase = (((time % FLOW_PERIOD) + FLOW_PERIOD) % FLOW_PERIOD) / FLOW_PERIOD;
    const palette = new Uint8Array(SOURCE_WIDTH * 3);
    for (let x = 0; x < SOURCE_WIDTH; x += 1) {
      const [r, g, b] = hueRgb(phase + x / SOURCE_WIDTH * 1.25);
      palette[x * 3] = r;
      palette[x * 3 + 1] = g;
      palette[x * 3 + 2] = b;
    }
    flowPalette = palette;
    return palette;
  };

  const drawFrame = (state, time) => {
    const palette = getPalette(time);
    const frame = state.frameData;
    frame.data.set(state.staticData.data);
    for (let i = 0; i < state.maskIndices.length; i += 1) {
      const index = state.maskIndices[i];
      const offset = index * 4;
      const alpha = state.maskAlpha[index];
      const oldAlpha = state.maskOldAlpha[index];
      const paletteOffset = state.maskXs[i] * 3;
      const baseAlpha = Math.max(0, Math.min(1, oldAlpha));
      const flowAlpha = Math.max(0, Math.min(1, alpha));
      const red = palette[paletteOffset];
      const green = palette[paletteOffset + 1];
      const blue = palette[paletteOffset + 2];
      if (state.config.emissionModel === 'source-red' && (oldAlpha > 0 || alpha > 0) && !(oldAlpha === 0 && state.maskReflection[index] > 0)) {
        const source = state.staticData.data;
        // Halo cannot desaturate neutral metal or recolour blue paint.
        if (oldAlpha < 0.99 && source[offset] <= Math.max(source[offset + 1], source[offset + 2])) continue;
        const sourceMin = Math.min(source[offset], source[offset + 1], source[offset + 2]);
        const neutral = Math.min(sourceMin, state.config.neutralWhiteCap ?? sourceMin);
        const emission = Math.max(0, source[offset] - neutral);
        const strength = oldAlpha >= 0.99 ? 1 : Math.max(0, Math.min(1, Math.max(alpha, oldAlpha)));
        const amount = emission * strength / 255;
        frame.data[offset] = clamp(source[offset] * (1 - strength) + (neutral + red * amount) * strength);
        frame.data[offset + 1] = clamp(source[offset + 1] * (1 - strength) + (neutral + green * amount) * strength);
        frame.data[offset + 2] = clamp(source[offset + 2] * (1 - strength) + (neutral + blue * amount) * strength);
        continue;
      }
      if (state.config.preserveNeutralRed && oldAlpha > 0) {
        const source = state.staticData.data;
        const sourceMin = Math.min(source[offset], source[offset + 1], source[offset + 2]);
        const neutral = state.config.neutralWhiteCap != null && sourceMin > 160 && source[offset] - sourceMin < 100 ? Math.min(sourceMin, state.config.neutralWhiteCap) : sourceMin;
        const emission = Math.max(0, source[offset] - neutral);
        frame.data[offset] = clamp(neutral + red * emission / 255);
        frame.data[offset + 1] = clamp(neutral + green * emission / 255);
        frame.data[offset + 2] = clamp(neutral + blue * emission / 255);
        continue;
      }
      const reflection = state.maskReflection[index];
      if (reflection > 0 && oldAlpha === 0) {
        frame.data[offset] = clamp(state.staticData.data[offset] - reflection + red * reflection / 255);
        frame.data[offset + 1] = clamp(state.staticData.data[offset + 1] + green * reflection / 255);
        frame.data[offset + 2] = clamp(state.staticData.data[offset + 2] + blue * reflection / 255);
        continue;
      }
      frame.data[offset] = clamp(state.staticData.data[offset] * (1 - baseAlpha) + red * flowAlpha);
      frame.data[offset + 1] = clamp(state.staticData.data[offset + 1] * (1 - baseAlpha) + green * flowAlpha);
      frame.data[offset + 2] = clamp(state.staticData.data[offset + 2] * (1 - baseAlpha) + blue * flowAlpha);
    }
    state.ctx.putImageData(frame, 0, 0);
    state.currentData = frame;
    state.frameCount += 1;
  };

  const resetCanvas = state => {
    if (!state.staticData || !state.ctx) return;
    state.ctx.putImageData(state.staticData, 0, 0);
    state.currentData = state.staticData;
  };
  const setStatus = (state, message) => {
    if (state.status) state.status.textContent = message;
  };

  const listen = (state, target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    state.cleanup.push(() => target.removeEventListener(type, handler, options));
  };

  const stopFlow = (state, message) => {
    activeStates.delete(state);
    if (!activeStates.size && schedulerFrame !== null) {
      window.cancelAnimationFrame?.(schedulerFrame);
      schedulerFrame = null;
    }
    state.running = false;
    state.owner = null;
    state.root.dataset.running = 'false';
    state.toggle?.setAttribute('aria-pressed', 'false');
    if (state.toggle) state.toggle.textContent = 'Preview Flow';
    resetCanvas(state);
    if (message) setStatus(state, message);
  };

  const cleanupState = state => {
    if (!state || !states.has(state)) return;
    stopFlow(state);
    state.observer?.disconnect?.();
    state.observer = null;
    state.cleanup.splice(0).forEach(remove => {
      try { remove(); } catch (_error) { /* detached section */ }
    });
    if (state.sourceImage) {
      state.sourceImage.onload = null;
      state.sourceImage.onerror = null;
      state.sourceImage.src = '';
    }
    state.sourceImage = null;
    states.delete(state);
    statesByRoot.delete(state.root);
    state.failed = true;
    state.ready = false;
    state.sourceData = null;
    state.staticData = null;
    state.frameData = null;
    state.currentData = null;
    state.maskAlpha = null;
    state.maskOldAlpha = null;
    state.maskIndices = null;
    state.maskXs = null;
    state.ctx = null;
  };

  const scheduleFrames = () => {
    if (schedulerFrame !== null || !activeStates.size) return;
    if (typeof window.requestAnimationFrame !== 'function') {
      [...activeStates].forEach(state => stopFlow(state, 'Flow preview is unavailable in this browser.'));
      return;
    }
    schedulerFrame = window.requestAnimationFrame(runFrames);
  };
  const runFrames = time => {
    schedulerFrame = null;
    if (hidden()) {
      [...activeStates].forEach(state => stopFlow(state, 'Static preview.'));
      return;
    }
    [...activeStates].forEach(state => {
      if (!state.ready || !state.inViewport || !state.root.isConnected) {
        stopFlow(state, 'Static preview.');
        return;
      }
      if (state.lastFrameTime === -Infinity || time - state.lastFrameTime >= FRAME_INTERVAL) {
        drawFrame(state, time);
        state.lastFrameTime = time;
      }
    });
    scheduleFrames();
  };
  const startFlow = (state, reason) => {
    if (!state.ready || state.failed || !state.inViewport || hidden()) return;
    if (reason === 'hover' && state.owner === 'button') return;
    if (isReducedMotion()) {
      stopFlow(state, 'Static preview; animation disabled by reduced-motion preference.');
      return;
    }
    state.running = true;
    state.owner = reason;
    state.lastFrameTime = -Infinity;
    state.root.dataset.running = 'true';
    state.toggle?.setAttribute('aria-pressed', 'true');
    if (state.toggle) state.toggle.textContent = 'Stop preview';
    setStatus(state, 'Flow preview running.');
    activeStates.add(state);
    scheduleFrames();
  };

  const unavailable = (state, message = 'Flow preview unavailable for this image.') => {
    state.failed = true;
    state.root.dataset.ready = 'false';
    state.root.dataset.running = 'false';
    if (state.toggle) {
      state.toggle.disabled = true;
      state.toggle.hidden = true;
    }
    setStatus(state, message);
  };

  const loadSource = (state, sourceUrl) => {
    if (!sourceUrl || typeof window.Image !== 'function') {
      unavailable(state);
      return;
    }
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    const finish = () => {
      if (state.failed) return;
      if (image.naturalWidth !== SOURCE_WIDTH || image.naturalHeight !== SOURCE_HEIGHT) {
        unavailable(state, 'Flow preview unavailable: source image dimensions changed.');
        return;
      }
      try {
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = SOURCE_WIDTH;
        sourceCanvas.height = SOURCE_HEIGHT;
        const sourceContext = sourceCanvas.getContext('2d', {willReadFrequently: true});
        if (!sourceContext) throw new Error('2D canvas unavailable');
        sourceContext.drawImage(image, 0, 0, SOURCE_WIDTH, SOURCE_HEIGHT);
        const sourceData = sourceContext.getImageData(0, 0, SOURCE_WIDTH, SOURCE_HEIGHT);
        state.sourceData = sourceData;
        state.staticData = sourceData;
        buildMask(state, sourceData);
        state.frameData = copyImageData(state.ctx, sourceData);
        state.currentData = state.staticData;
        state.ctx.putImageData(state.staticData, 0, 0);
        state.ready = true;
        state.root.dataset.ready = 'true';
        state.root.dataset.running = 'false';
        state.toggle.disabled = false;
        state.toggle.hidden = false;
        state.toggle.setAttribute('aria-pressed', 'false');
        state.toggle.textContent = 'Preview Flow';
        setStatus(state, 'Static preview.');
      } catch (_error) {
        unavailable(state, 'Flow preview unavailable: this image cannot be read securely.');
      }
    };
    image.onload = finish;
    image.onerror = () => unavailable(state);
    image.src = sourceUrl;
    state.sourceImage = image;
  };

  const resolveStates = target => {
    if (!target) return [...states];
    if (target && target.nodeType === 1) {
      const state = statesByRoot.get(target);
      return state ? [state] : [];
    }
    if (typeof target === 'string') {
      return [...states].filter(state => state.key === target || state.kind === target || state.brand === normaliseBrand(target));
    }
    return target.root && states.has(target) ? [target] : [];
  };
  const diagnostic = target => {
    const selected = resolveStates(target);
    const make = state => ({
      brand: state.brand,
      kind: state.kind,
      key: state.key,
      status: state.failed ? 'unavailable' : state.ready ? (state.running ? 'running' : 'ready') : 'loading',
      ready: state.ready,
      running: state.running,
      maskCount: state.maskCount,
      frameCount: state.frameCount,
      source: basename(state.sourceUrl),
      sourceWidth: state.sourceImage?.naturalWidth || 0,
      sourceHeight: state.sourceImage?.naturalHeight || 0,
    });
    if (target) return make(selected[0] || {brand: '', kind: '', key: String(target), failed: true, ready: false, running: false, maskCount: 0, frameCount: 0, sourceUrl: ''});
    return Object.freeze(Object.fromEntries(selected.map(state => [state.key, make(state)])));
  };
  const assertOutsideMaskUnchanged = target => {
    const selected = resolveStates(target);
    const results = selected.map(state => {
      if (!state.staticData || !state.ctx) return {key: state.key, ready: false, ok: false, checked: 0, differences: 0};
      let current;
      try { current = state.ctx.getImageData(0, 0, SOURCE_WIDTH, SOURCE_HEIGHT); } catch (_error) {
        return {key: state.key, ready: state.ready, ok: false, checked: 0, differences: 0};
      }
      let checked = 0;
      let differences = 0;
      for (let index = 0; index < PIXEL_COUNT; index += 1) {
        if (state.maskAlpha[index] > 0 || state.maskOldAlpha[index] > 0) continue;
        checked += 1;
        const offset = index * 4;
        for (let channel = 0; channel < 4; channel += 1) {
          if (current.data[offset + channel] !== state.staticData.data[offset + channel]) {
            differences += 1;
            break;
          }
        }
      }
      return {key: state.key, ready: state.ready, ok: differences === 0, checked, differences};
    });
    return target ? results[0] || {ready: false, ok: false, checked: 0, differences: 0} : results;
  };
  const renderPhase = (target, phase = 0) => {
    const state = resolveStates(target)[0];
    if (!state?.ready) return {ready: false, rendered: false};
    const numericPhase = Number.isFinite(Number(phase)) ? Number(phase) : 0;
    drawFrame(state, ((numericPhase % 1) + 1) % 1 * FLOW_PERIOD);
    return {ready: true, rendered: true, phase: ((numericPhase % 1) + 1) % 1, frameCount: state.frameCount, key: state.key};
  };

  const diagnostics = target => diagnostic(target);
  Object.defineProperties(diagnostics, {
    get: {value: diagnostic},
    snapshot: {value: diagnostic},
    renderPhase: {value: renderPhase},
    assertOutsideMaskUnchanged: {value: assertOutsideMaskUnchanged},
    status: {get: () => diagnostic()},
  });
  Object.freeze(diagnostics);
  try {
    if (!Object.prototype.hasOwnProperty.call(window, '__LDS_BRAND_HUB_FLOW__')) {
      Object.defineProperty(window, '__LDS_BRAND_HUB_FLOW__', {
        configurable: false,
        enumerable: false,
        writable: false,
        value: diagnostics,
      });
    }
  } catch (_error) {
    // A locked host object must not prevent the static banner from rendering.
  }

  const init = root => {
    if (!root || statesByRoot.has(root)) return;
    const {brand, kind} = resolveKindBrand(root);
    const config = CONFIG[brand]?.[kind];
    if (!config) return;
    const image = root.querySelector('img');
    const canvas = root.querySelector('canvas');
    const stage = root.querySelector('[data-brand-flow-stage], [data-banner-flow-stage]') || canvas?.parentElement;
    const toggle = root.querySelector('[data-brand-flow-toggle], [data-banner-flow-toggle]');
    const status = root.querySelector('[data-brand-flow-status], [data-banner-flow-status]');
    if (!image || !canvas || !stage || !toggle || !status) return;

    const sourceUrl = image.currentSrc || image.getAttribute('src') || image.src || '';
    const state = {
      root,
      brand,
      kind,
      key: `${brand}:${kind}:${states.size + 1}`,
      config,
      image,
      canvas,
      stage,
      toggle,
      status,
      sourceUrl,
      sourceImage: null,
      sourceData: null,
      staticData: null,
      frameData: null,
      currentData: null,
      ctx: null,
      maskAlpha: new Float32Array(PIXEL_COUNT),
      maskOldAlpha: new Float32Array(PIXEL_COUNT),
      maskIndices: new Uint32Array(0),
      maskXs: new Uint16Array(0),
      maskCount: 0,
      frameCount: 0,
      ready: false,
      failed: false,
      running: false,
      owner: null,
      inViewport: true,
      lastFrameTime: -Infinity,
      observer: null,
      cleanup: [],
    };
    states.add(state);
    statesByRoot.set(root, state);
    root.dataset.ready = 'false';
    root.dataset.running = 'false';
    canvas.width = SOURCE_WIDTH;
    canvas.height = SOURCE_HEIGHT;
    canvas.setAttribute('aria-hidden', 'true');
    toggle.type = 'button';
    toggle.setAttribute('aria-pressed', 'false');
    toggle.disabled = true;
    toggle.textContent = 'Preview Flow';
    setStatus(state, 'Loading preview.');

    try {
      state.ctx = canvas.getContext('2d', {willReadFrequently: true});
      if (!state.ctx) throw new Error('2D canvas unavailable');
    } catch (_error) {
      unavailable(state, 'Flow preview unavailable in this browser.');
      return;
    }
    if (!config.names.includes(basename(sourceUrl))) {
      unavailable(state, 'Flow preview unavailable for this source image.');
      return;
    }

    const onPointerEnter = event => {
      if (event?.pointerType === 'touch' || !hoverFine()) return;
      startFlow(state, 'hover');
    };
    const onPointerLeave = () => {
      if (state.owner === 'hover') stopFlow(state, 'Static preview.');
    };
    listen(state, stage, 'pointerenter', onPointerEnter);
    listen(state, stage, 'pointerleave', onPointerLeave);
    listen(state, stage, 'mouseenter', onPointerEnter);
    listen(state, stage, 'mouseleave', onPointerLeave);
    const onFocusIn = event => {
      if (event.target !== toggle) startFlow(state, 'focus');
    };
    const onFocusOut = event => {
      if (state.owner === 'focus' && !root.contains(event.relatedTarget)) stopFlow(state, 'Static preview.');
    };
    listen(state, root, 'focusin', onFocusIn);
    listen(state, root, 'focusout', onFocusOut);
    const onToggleClick = () => {
      if (!state.ready) return;
      if (state.running && state.owner === 'button') stopFlow(state, 'Static preview.');
      else if (state.running) stopFlow(state, 'Static preview.');
      else startFlow(state, 'button');
    };
    listen(state, toggle, 'click', onToggleClick);

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const onMotionChange = event => {
      if (event.matches) stopFlow(state, 'Static preview; animation disabled by reduced-motion preference.');
    };
    if (motionQuery?.addEventListener) {
      motionQuery.addEventListener('change', onMotionChange);
      state.cleanup.push(() => motionQuery.removeEventListener('change', onMotionChange));
    } else if (motionQuery?.addListener) {
      motionQuery.addListener(onMotionChange);
      state.cleanup.push(() => motionQuery.removeListener(onMotionChange));
    }
    const onVisibilityChange = () => {
      if (hidden()) stopFlow(state, 'Static preview.');
    };
    listen(state, document, 'visibilitychange', onVisibilityChange);
    if (typeof window.IntersectionObserver === 'function') {
      state.observer = new window.IntersectionObserver(entries => {
        state.inViewport = Boolean(entries[0]?.isIntersecting);
        if (!state.inViewport) stopFlow(state, 'Static preview.');
      }, {threshold: 0});
      state.observer.observe(root);
    }
    loadSource(state, sourceUrl);
  };

  const initAll = () => {
    // A theme editor can detach a section without delivering unload before a
    // reload.  Reclaim those states before discovering the replacement roots.
    [...states].filter(state => !state.root.isConnected).forEach(cleanupState);
    document.querySelectorAll(FLOW_SELECTOR).forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once: true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', event => {
    const section = event.target;
    [...states]
      .filter(state => !state.root.isConnected || section === state.root || section?.contains?.(state.root))
      .forEach(cleanupState);
  });
})();
